import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { IMfaApiResponse, TMfaActionType } from './mfaTypes';

interface IResendMfaOtpInput {
  actionType: Exclude<TMfaActionType, null>;
}

const initialState: IApiState<IMfaApiResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const resendMfaOtpAsync = createAsyncThunk(
  'settings/mfa/resend',
  async (
    { actionType }: IResendMfaOtpInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.post<IMfaApiResponse>(
        '/auth/mfa/resend',
        { actionType },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'OTP has been resent',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IMfaApiResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description:
            error?.response?.data?.message ??
            'Could not resend OTP. Please try again.',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const resendMfaOtpSlice = createSlice({
  name: 'settings/mfa/resend',
  initialState,
  reducers: {
    resetResendMfaOtp: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resendMfaOtpAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(resendMfaOtpAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(resendMfaOtpAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IMfaApiResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetResendMfaOtp } = resendMfaOtpSlice.actions;

export default resendMfaOtpSlice.reducer;
